import { api } from './api'
import { toast } from 'sonner'
import { processRetentionData, RetentionDataPoint } from '@/utils/retention-curve'

export interface AppMetrics {
  app: string
  ua_spent: number
  installs: number
  cpi: number
  retention_d1?: number
  retention_d7?: number
  retention_d30?: number
  ad_revenue: number
  dau: number
  ad_impressions: number
  iap_revenue: number
}

interface DataRequest {
  output?: 'csv' | 'json'  // csv: strings, no type conversion; json: typed (metrics as floats, dimensions as text)
  organization_id?: number
  metrics?: string[]
  dimensions?: string[]
  period?: string | string[]
  limit?: number
  offset?: number
  search?: string
}

class OptimizerService {
  private lastCsvUrl?: string
  private lastRequestParams?: Omit<DataRequest, 'output'>

  async fetchAppMetrics(): Promise<AppMetrics[]> {
    const metrics = [
      'ua_spent',
      'installs',
      'cpi',
      'ad_revenue',
      'dau',
      'ad_impressions',
      'iap_revenue'
    ]

    const requestParams: Omit<DataRequest, 'output'> = {
      metrics,
      dimensions: ['app'],
      period: ['d-30', 'd-1'], // Rolling 30 days window
    }

    // Store params for on-demand CSV download
    this.lastRequestParams = requestParams

    const requestBody: DataRequest = {
      output: 'json', // Typed output: metrics as floats, dimensions as text
      ...requestParams,
    }

    // Note: organization_id is now handled via authentication headers (X-Organization-Slug)
    // and is resolved by the backend from the authenticated user/organization context

    try {
      console.log('🔍 Edge: Fetching app metrics with payload:', requestBody)
      const response = await api.post('/data/retrieve', requestBody)
      console.log('📊 Edge: Response received:', response?.data)

      if (response?.data) {
        // Check for error response from optimizer service
        if (response.data.status === 'error') {
          const errorDetails = {
            message: response.data.message || 'Unknown error',
            errors: response.data.errors || [],
            organization_id: response.data.organization_id
          }
          
          // Display error using toast notification (with ID to prevent duplicates)
          toast.error("Optimizer API Error", {
            description: `${errorDetails.message}\n\nErrors:\n${errorDetails.errors.join('\n')}\n\nOrg ID: ${errorDetails.organization_id}`,
            duration: 10000, // Show for 10 seconds
            id: 'optimizer-api-error', // Prevent duplicate toasts
            style: {
              background: 'rgb(254 242 242)', // bg-red-50
              color: 'rgb(127 29 29)', // text-red-900
              border: '1px solid rgb(239 68 68)', // border-red-500
            }
          })
          
          return []
        }
        
        // Store CSV URL if provided
        if (response.data.csv_url || response.data.file_url) {
          this.lastCsvUrl = response.data.csv_url || response.data.file_url
        }

        if (response.data.data) {
          // Transform the response data to our expected format
          const transformedData = response.data.data.map((item: any) => ({
            app: item.app || item.name || 'Unknown App',
            ua_spent: item.ua_spent || 0,
            installs: item.installs || 0,
            cpi: item.cpi || 0,
            ad_revenue: item.ad_revenue || 0,
            dau: item.dau || 0,
            ad_impressions: item.ad_impressions || 0,
            iap_revenue: item.iap_revenue || 0,
          }))
          return transformedData
        }
      }
      
      return []
    } catch (error: any) {
      console.error('❌ Edge: Error fetching app metrics:', error)
      // Also check if the error response contains optimizer error details
      if (error.response?.data?.status === 'error') {
        const errorDetails = error.response.data
        toast.error(`Optimizer API Error (HTTP ${error.response.status})`, {
          description: `${errorDetails.message}\n\nErrors:\n${errorDetails.errors?.join('\n') || 'No specific errors'}\n\nOrg ID: ${errorDetails.organization_id}`,
          duration: 10000,
          id: 'optimizer-api-error', // Prevent duplicate toasts
          style: {
            background: 'rgb(254 242 242)', // bg-red-50
            color: 'rgb(127 29 29)', // text-red-900
            border: '1px solid rgb(239 68 68)', // border-red-500
          }
        })
      }
      return []
    }
  }

  async fetchRetentionData(appName?: string): Promise<RetentionDataPoint[]> {
    const requestBody: any = {
      output: 'json', // Typed output: metrics as floats, dimensions as text
      metrics: ['cohort_active_users', 'cohort_total_users'],
      dimensions: ['retention_day'],
      period: ['d-60', 'd-0'], // Extended period to capture retention_day 30 data
    }

    // Add conditional filter for specific app
    if (appName && appName !== 'All Apps') {
      requestBody.conditional = `app = "${appName}"`
    }

    try {
      const response = await api.post('/data/retrieve', requestBody)

      if (response?.data?.data) {
        return processRetentionData(response.data.data)
      }

      return []
    } catch (error: any) {
      console.error('Failed to fetch retention data:', error)
      toast.warning('Could not load retention data, using defaults')
      return []
    }
  }

  getLastCsvUrl(): string | undefined {
    return this.lastCsvUrl
  }

  hasRequestParams(): boolean {
    return !!this.lastRequestParams
  }

  async fetchCsvUrl(): Promise<string | undefined> {
    if (!this.lastRequestParams) {
      toast.error('No data request available. Please load app data first.')
      return undefined
    }

    try {
      const response = await api.post('/data/retrieve', {
        output: 'csv',
        ...this.lastRequestParams,
      })

      if (response?.data) {
        const csvUrl = response.data.csv_url || response.data.file_url
        if (csvUrl) {
          this.lastCsvUrl = csvUrl
          return csvUrl
        }
      }

      toast.error('CSV URL not available in response')
      return undefined
    } catch (error: any) {
      console.error('Failed to fetch CSV:', error)
      toast.error('Failed to generate CSV download')
      return undefined
    }
  }

  calculateAggregatedMetrics(data: AppMetrics[]): AppMetrics {
    if (data.length === 0) {
      return {
        app: 'All Apps',
        ua_spent: 0,
        installs: 0,
        cpi: 0,
        ad_revenue: 0,
        dau: 0,
        ad_impressions: 0,
        iap_revenue: 0,
      }
    }

    // Sum metrics where appropriate
    const aggregated = data.reduce((acc, curr) => {
      return {
        app: 'All Apps',
        ua_spent: acc.ua_spent + (curr.ua_spent || 0),
        installs: acc.installs + (curr.installs || 0),
        cpi: 0, // Will calculate after
        ad_revenue: acc.ad_revenue + (curr.ad_revenue || 0),
        dau: acc.dau + (curr.dau || 0),
        ad_impressions: acc.ad_impressions + (curr.ad_impressions || 0),
        iap_revenue: acc.iap_revenue + (curr.iap_revenue || 0),
      }
    }, {
      app: 'All Apps',
      ua_spent: 0,
      installs: 0,
      cpi: 0,
      ad_revenue: 0,
      dau: 0,
      ad_impressions: 0,
      iap_revenue: 0,
    } as AppMetrics)

    // Calculate CPI from total spend / total installs
    const calculatedCPI = aggregated.installs > 0 ? aggregated.ua_spent / aggregated.installs : 0

    return {
      app: 'All Apps',
      ua_spent: aggregated.ua_spent,
      installs: aggregated.installs,
      cpi: calculatedCPI,
      ad_revenue: aggregated.ad_revenue,
      dau: aggregated.dau,
      ad_impressions: aggregated.ad_impressions,
      iap_revenue: aggregated.iap_revenue,
    }
  }
}

export const optimizerService = new OptimizerService()